import React, { useState } from "react";
import "../../App.css";
import { useApp } from "../../core/context";
import { uuid } from "../../core/helpers";
import { FIELD_TYPES } from "../../core/constants";
import { ORG_JSON_KEYS, persistOrgJsonSetting } from "../../core/org-json-settings";
import { Icon } from "../../components/Icon";

const ENTITIES = [
  { id: "clients", label: "Clients", example: "Authorised Capital, Paid-up Capital" },
  { id: "tasks", label: "Tasks", example: "ROC SRN, Filing reference" },
  { id: "employees", label: "Employees", example: "Employee ID, Department" },
];

const blankField = () => ({ id: uuid(), label: "", type: "text", required: false, options: "" });

function CustomFieldsSettings() {
  const { org, setOrg, showToast } = useApp();
  const [entity, setEntity] = useState("clients");
  const [saving, setSaving] = useState(false);
  const all = org.customFields || { clients: [], tasks: [], employees: [] };
  const fields = all[entity] || [];

  const setFields = (next) => {
    setOrg(prev => ({ ...prev, customFields: { ...all, [entity]: next } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...all, [entity]: fields };
      await persistOrgJsonSetting(ORG_JSON_KEYS.customFields, payload, setOrg, "customFields");
      showToast("Custom fields saved!", "success");
    } catch (e) {
      showToast(e.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const upd = (idx, k, v) => setFields(fields.map((f, i) => (i === idx ? { ...f, [k]: v } : f)));
  const meta = ENTITIES.find(e => e.id === entity);

  return (
    <div style={{ maxWidth: 780 }}>
      <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 16 }}>
        Define extra fields for clients, tasks, and employees. They appear on create/edit forms and detail views.
      </div>
      <div className="chips" style={{ marginBottom: 16 }}>
        {ENTITIES.map(e => (
          <div key={e.id} className={`chip ${entity === e.id ? "on" : ""}`} onClick={() => setEntity(e.id)}>{e.label}</div>
        ))}
      </div>
      <div className="card">
        <div className="card-head">
          <div>
            <div className="card-title">{meta?.label} — Custom Fields</div>
            <div style={{ fontSize: 12, color: "var(--muted)" }}>e.g. {meta?.example}</div>
          </div>
          <button className="btn btn-sm btn-primary" onClick={() => setFields([...fields, blankField()])}>+ Add Field</button>
        </div>
        <div className="card-body">
          {!fields.length && (
            <div style={{ padding: 24, textAlign: "center", color: "var(--muted)", fontSize: 13 }}>No custom fields yet.</div>
          )}
          {fields.map((f, i) => (
            <div key={f.id} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 10, padding: 12, background: "#FAFAF8", borderRadius: 8, border: "1px solid var(--border)" }}>
              <div style={{ flex: 2 }}>
                <label className="f-label" style={{ fontSize: 9 }}>Field name</label>
                <input className="f-input" value={f.label} onChange={e => upd(i, "label", e.target.value)} placeholder="Authorised Capital" />
              </div>
              <div style={{ flex: 1 }}>
                <label className="f-label" style={{ fontSize: 9 }}>Type</label>
                <select className="f-select" value={f.type} onChange={e => upd(i, "type", e.target.value)}>
                  {FIELD_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label className="f-label" style={{ fontSize: 9 }}>Required</label>
                <select className="f-select" value={f.required ? "yes" : "no"} onChange={e => upd(i, "required", e.target.value === "yes")}>
                  <option value="no">Optional</option>
                  <option value="yes">Required</option>
                </select>
              </div>
              {f.type === "select" && (
                <div style={{ flex: 2 }}>
                  <label className="f-label" style={{ fontSize: 9 }}>Options (comma-separated)</label>
                  <input className="f-input" value={f.options || ""} onChange={e => upd(i, "options", e.target.value)} placeholder="Option A, Option B" />
                </div>
              )}
              <button type="button" className="btn btn-sm btn-red" style={{ marginTop: 18 }} onClick={() => setFields(fields.filter((_, j) => j !== i))}>
                <Icon name="close" size={12} />
              </button>
            </div>
          ))}
        </div>
        <div style={{ padding: "0 20px 20px" }}>
          <button className="btn btn-primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save Fields"}</button>
        </div>
      </div>
    </div>
  );
}

export { CustomFieldsSettings };
